import * as React from "react"
import { Loader2 } from "lucide-react"

import { cn } from "@/shared/lib/utils"

export interface PrimaryButtonLProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean
  /** Solo lime vs squad yellow — same Primary L geometry; hover tints differ. */
  tone?: "lime" | "squad"
  /** `l` = 60px / 16px (bar); `sheet` = 54px / 17px (wallet drawers). */
  size?: "l" | "sheet"
  /**
   * Inactive CTA (e.g. “Enter amount”) — `bg-white/10`, `text-white/30`, not accent.
   * Mutually exclusive with accent hover/loading ink spinner (spinner uses white when muted+loading).
   */
  muted?: boolean
}

/**
 * Design system: **Button Primary L** — full-width CTA, radius 16px, Title/16-Bold (or 17px in `sheet`).
 * Accent: lime `#B1FE64`, hover/focus `#CDFF9B`; disabled accent @ 40% opacity; loading → ink spinner ~16.5px.
 */
export const PrimaryButtonL = React.forwardRef<
  HTMLButtonElement,
  PrimaryButtonLProps
>(
  (
    {
      className,
      loading = false,
      disabled,
      children,
      type = "button",
      tone = "lime",
      size = "l",
      muted = false,
      ...props
    },
    ref
  ) => {
    const accentSurface =
      tone === "squad"
        ? cn(
            "bg-[var(--ds-primary-l-squad-bg)] text-[var(--ds-secondary-m-fg)]",
            "active:bg-[var(--ds-primary-l-squad-bg-hover)] [@media(hover:hover)]:hover:bg-[var(--ds-primary-l-squad-bg-hover)]"
          )
        : cn(
            "bg-[var(--ds-primary-l-bg)] text-[var(--ds-secondary-m-fg)]",
            "active:bg-[var(--ds-primary-l-bg-hover)] [@media(hover:hover)]:hover:bg-[var(--ds-primary-l-bg-hover)]"
          )

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        data-loading={loading ? "" : undefined}
        aria-busy={loading || undefined}
        className={cn(
          "inline-flex w-full min-w-0 items-center justify-center rounded-2xl font-bold transition-colors focus-visible:outline-none",
          size === "l"
            ? "h-[60px] text-base leading-[19px] tracking-[-0.5px]"
            : "h-[54px] text-[17px] tracking-[-0.3px]",
          muted
            ? "bg-white/10 text-white/30 active:bg-white/10 [@media(hover:hover)]:hover:bg-white/10"
            : accentSurface,
          !muted && "disabled:opacity-40 data-[loading]:opacity-100",
          muted && "disabled:opacity-100",
          className
        )}
        {...props}
      >
        {loading ? (
          <Loader2
            className={cn(
              "shrink-0 animate-spin",
              muted
                ? "size-4 text-white/60"
                : "h-[16.5px] w-[16.5px] text-[var(--ds-secondary-m-fg)]"
            )}
            strokeWidth={2}
            aria-hidden
          />
        ) : (
          children
        )}
      </button>
    )
  }
)
PrimaryButtonL.displayName = "PrimaryButtonL"
